import { Wordmark } from "../components/Logo.tsx";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Checkbox, Field, Input, MessageBar, MessageBarBody, Spinner, Text, ToggleButton, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { ClientSelfSignupInput, type ClientPreferenceInput } from "@gymplan/shared";
import { api, type AvailabilitySlot } from "../lib/api.ts";
import { DayChips } from "../components/DayChips.tsx";

interface InviteInfo {
  trainerName: string;
  sessionLengthMinutes: number;
  gyms: { id: string; name: string; isPrimary: boolean }[];
  availability: AvailabilitySlot[];
}

const useStyles = makeStyles({
  page: { minHeight: "100vh", backgroundColor: tokens.colorNeutralBackground2, display: "flex", justifyContent: "center" },
  card: { width: "100%", maxWidth: "520px", padding: "20px", display: "flex", flexDirection: "column", gap: "14px", boxSizing: "border-box" },
  brand: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForeground1 },
  progress: { display: "flex", gap: "4px" },
  dot: { flex: 1, height: "4px", borderRadius: "2px", backgroundColor: tokens.colorNeutralStroke2 },
  dotOn: { backgroundColor: tokens.colorBrandBackground },
  big: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "6px" },
  actions: { display: "flex", gap: "8px", marginTop: "8px", position: "sticky", bottom: 0, padding: "12px 0", backgroundColor: tokens.colorNeutralBackground2 },
});

const STEPS = ["Welcome", "About you", "How often", "When", "Confirm"] as const;

/** Phone-first client sign-up: one question per screen. */
export function JoinPage() {
  const s = useStyles();
  const { token } = useParams();
  const info = useQuery({ queryKey: ["invite", token], queryFn: () => api.get<InviteInfo>(`/api/clients/invite/${token}`), retry: false });
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: "", phone: "", email: "", sessionsPerWeek: 2, preferences: [] as ClientPreferenceInput[], acceptPrivacy: false, confirmAdult: false, whatsappOptIn: true });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  if (info.isLoading) return <Shell s={s}><Spinner /></Shell>;
  if (info.isError || !info.data) return <Shell s={s}><Text size={500} weight="semibold">This link isn't valid</Text><Text>Ask your trainer for a fresh one.</Text></Shell>;
  const trainer = info.data.trainerName;

  if (done)
    return (
      <Shell s={s}>
        <Text size={600} weight="semibold">You're in 🎉</Text>
        <Text>{trainer} can now see when you can train.</Text>
        <Text>Each week you'll get a WhatsApp asking if your usual times still work — just reply.</Text>
        <Text weight="semibold">Your personal link (to change anything later):</Text>
        <Text size={200} style={{ wordBreak: "break-all" }}><a href={done}>{done}</a></Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>We'll send it to you on WhatsApp too.</Text>
      </Shell>
    );

  const next = () => {
    setErr(null);
    if (step === 1) {
      if (f.name.trim().length < 2) return setErr("Please enter your name.");
      if (!/^\+[1-9]\d{6,14}$/.test(f.phone.replace(/\s+/g, ""))) return setErr("Enter your WhatsApp number with the country code, e.g. +44 7700 900123.");
    }
    if (step === 3 && f.preferences.length === 0) return setErr("Pick at least one day.");
    setStep(step + 1);
  };

  const submit = async () => {
    setErr(null);
    const parsed = ClientSelfSignupInput.safeParse({ ...f, phone: f.phone.replace(/\s+/g, ""), email: f.email || undefined, inviteToken: token });
    if (!parsed.success) return setErr(parsed.error.issues[0]?.message ?? "Please check the form.");
    if (!f.acceptPrivacy || !f.confirmAdult) return setErr("Please tick the two boxes to continue.");
    setBusy(true);
    try {
      const r = await api.post<{ portalUrl: string }>("/api/clients/invite", parsed.data);
      setDone(r.portalUrl);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell s={s}>
      <div className={s.progress}>
        {STEPS.map((_, i) => <div key={i} className={`${s.dot} ${i <= step ? s.dotOn : ""}`} />)}
      </div>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Step {step + 1} of {STEPS.length} · {STEPS[step]}</Text>

      {step === 0 && (
        <>
          <Text size={600} weight="semibold">Train with {trainer}</Text>
          <Text>{trainer} uses RackUp to plan sessions. Tell us when you can train — about a minute — and you'll get a WhatsApp each week to confirm.</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Sessions are {info.data.sessionLengthMinutes} minutes{info.data.gyms[0] ? ` at ${info.data.gyms[0].name}` : ""}.</Text>
        </>
      )}

      {step === 1 && (
        <>
          <Text size={500} weight="semibold">About you</Text>
          <Field label="Your name" required>
            <Input size="large" autoComplete="name" value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
          </Field>
          <Field label="WhatsApp number" required hint="With country code, e.g. +44 7700 900123">
            <Input size="large" type="tel" autoComplete="tel" inputMode="tel" value={f.phone} onChange={(_, d) => setF({ ...f, phone: d.value })} />
          </Field>
          <Field label="Email (optional)">
            <Input size="large" type="email" autoComplete="email" inputMode="email" value={f.email} onChange={(_, d) => setF({ ...f, email: d.value })} />
          </Field>
        </>
      )}

      {step === 2 && (
        <>
          <Text size={500} weight="semibold">How many sessions a week?</Text>
          <div className={s.big}>
            {[1, 2, 3, 4, 5].map((n) => (
              <ToggleButton key={n} size="large" style={{ minWidth: 0 }} checked={f.sessionsPerWeek === n} onClick={() => setF({ ...f, sessionsPerWeek: n })}>
                {n}
              </ToggleButton>
            ))}
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <Text size={500} weight="semibold">When can you train?</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Tap the parts of each day that usually work. You can change this any time.</Text>
          <DayChips value={f.preferences} onChange={(preferences) => setF({ ...f, preferences })} availability={info.data.availability} />
        </>
      )}

      {step === 4 && (
        <>
          <Text size={500} weight="semibold">Nearly done</Text>
          <Text size={200}>{f.name} · {f.phone} · {f.sessionsPerWeek}×/week · {new Set(f.preferences.map((p) => p.weekday)).size} day(s)</Text>
          <Checkbox checked={f.acceptPrivacy} onChange={(_, d) => setF({ ...f, acceptPrivacy: !!d.checked })} label={<>I've read the <Link to="/privacy" target="_blank">privacy notice</Link> and agree to {trainer} using RackUp to schedule my sessions.</>} />
          <Checkbox checked={f.confirmAdult} onChange={(_, d) => setF({ ...f, confirmAdult: !!d.checked })} label="I'm 18 or over." />
          <Checkbox checked={f.whatsappOptIn} onChange={(_, d) => setF({ ...f, whatsappOptIn: !!d.checked })} label="Send me WhatsApp messages about my sessions (reply STOP any time)." />
        </>
      )}

      {err && (
        <MessageBar intent="error">
          <MessageBarBody>{err}</MessageBarBody>
        </MessageBar>
      )}

      <div className={s.actions}>
        {step > 0 && <Button size="large" appearance="secondary" icon={<ArrowLeft20Regular />} onClick={() => setStep(step - 1)} aria-label="Back" />}
        {step < STEPS.length - 1 ? (
          <Button size="large" appearance="primary" style={{ flex: 1 }} onClick={next}>
            {step === 0 ? "Let's go" : "Continue"}
          </Button>
        ) : (
          <Button size="large" appearance="primary" style={{ flex: 1 }} onClick={submit} disabled={busy}>
            Join
          </Button>
        )}
      </div>
    </Shell>
  );
}

function Shell({ s, children }: { s: ReturnType<typeof useStyles>; children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <div className={s.card}>
        <Wordmark height={36} />
        {children}
      </div>
    </div>
  );
}
